// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

/**
 * Converts a hexadecimal color code to an RGBA color string.
 *
 * @example
 *
 * ```typescript
 * const rgbaColor = hexToRgba('#ff5733', 0.5);
 * console.log(rgbaColor); // Output: "rgba(255, 87, 51, 0.5)"
 * ```
 *
 * @param hex - The hexadecimal color code (e.g., "#RRGGBB").
 * @param alpha - The alpha (opacity) value, ranging from 0 (fully transparent) to 1
 *   (fully opaque).
 * @returns The RGBA color string in the format `rgba(r, g, b, alpha)`.
 */
export const hexToRgba = (hex: string, alpha: number): string => {
  if (!/^#([0-9A-Fa-f]{6})$/.test(hex)) {
    throw new Error('Invalid hex color format. Expected format: #RRGGBB.');
  }
  if (alpha < 0 || alpha > 1) {
    throw new Error('Alpha value must be between 0 and 1.');
  }
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
